import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { withAuthProviderBarrier } from 'auth/AuthProviderBarrier';
import { AuthProvider } from 'auth/AuthProvider';
import './i18n/i18n';
import { Loading } from 'components/shared/Loading';
import '@ui5/webcomponents-react';
import { WebAssistantProvider } from 'components/shared/WebAssistant/useWebAssistant';
import { Helmet } from 'react-helmet';
import { setLanguage } from '@ui5/webcomponents-base/dist/config/Language';

// MSAL imports
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult
} from '@azure/msal-browser';
import { msalConfig } from 'auth/msalConfig';

// Fix calendar issue
import '@ui5/webcomponents/dist/Assets.js';
import '@ui5/webcomponents-fiori/dist/Assets.js';
import '@ui5/webcomponents-react/dist/Assets';
import * as manageServiceWorker from 'utils/manageServiceWorker';

import BusyIndicatorSize from '@ui5/webcomponents/dist/types/BusyIndicatorSize.js';

const msalInstance = new PublicClientApplication(msalConfig);
const SecureApp = withAuthProviderBarrier(App, msalInstance);
setLanguage('en');

const Application: React.FC = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<Loading size={BusyIndicatorSize.L} />}>
        <AuthProvider pca={msalInstance}>
          <SecureApp />
        </AuthProvider>
      </Suspense>
    </React.StrictMode>
  );
};

msalInstance.initialize().then(() => {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

  root.render(
    <>
      <Helmet htmlAttributes={{ lang: 'en' }}>
        <meta
          httpEquiv="Content-Security-Policy"
          content="script-src 'self' https://webassistant.enable-now.cloud.sap/web_assistant/framework/wpb/Help4.js 'unsafe-inline' 'unsafe-eval'"
        />
      </Helmet>
      <WebAssistantProvider>
        <Application />
      </WebAssistantProvider>
    </>
  );
});

manageServiceWorker.uninstall();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
